<template>
  <div>
    <div class="row">&nbsp;</div>
    <div class="container-fluid">
      <div class="card">
        <div v-if="is_authorization === null" class="card-body">
          <div class="d-flex justify-content-center">
            authorization checking...
          </div>
        </div>

        <div v-else-if="is_authorization === 'N'" class="card-body">
          <div class="d-flex justify-content-center">
            คุณไม่มีสิทธิ์เข้าถึงหน้านี้ กรุณาติดต่อแอดมิน
          </div>
        </div>

        <div v-else class="card-body p-0 realtime-body">
          <!-- Hero Header -->
          <div class="hero-header">
            <div class="hero-left">
              <div class="live-badge">
                <span class="live-dot"></span> LIVE
              </div>
              <h3 class="hero-title">{{ eventDetails.title || $route.params.event_key }}</h3>
              <div class="hero-sub">สรุปยอดผู้สมัครแบบเรียลไทม์</div>
              <div class="hero-updated" v-if="lastUpdatedAt">
                <vue-feather type="clock" size="13"></vue-feather>
                อัปเดตล่าสุด {{ lastUpdatedAt }}
              </div>
            </div>
            <div class="hero-right">
              <button type="button" @click.prevent="load_runner_type()" class="btn btn-primary btn-sm hero-refresh" :disabled="is_loading_runner_type === 'Y'">
                <vue-feather type="refresh-cw" size="15" :class="{ spinning: is_loading_runner_type === 'Y' }"></vue-feather>
                รีเฟรช
              </button>
              <div class="auto-refresh-toggle">
                <div class="form-check form-switch m-0 d-flex align-items-center">
                  <input
                    class="form-check-input me-2"
                    type="checkbox"
                    id="autoRefreshSwitch"
                    v-model="isAutoRefreshEnabled"
                    @change="onAutoRefreshSwitchChange"
                  />
                  <label class="form-check-label small mb-0" for="autoRefreshSwitch">
                    Auto Refresh <span class="opacity-75">(3 วิ)</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div class="realtime-content">
            <!-- Overall summary with donut -->
            <div v-if="report_runner_type.length > 0" class="summary-card">
              <div class="view-count-badge">
                <vue-feather type="eye" size="13"></vue-feather>
                {{ view_count.toLocaleString() }} ผู้เข้าชม
              </div>
              <div class="donut-wrap">
                <svg viewBox="0 0 120 120" class="donut-svg">
                  <circle class="donut-track" cx="60" cy="60" r="52" />
                  <circle
                    class="donut-value"
                    cx="60" cy="60" r="52"
                    :stroke="getStatusHex(getOverallPercent())"
                    :stroke-dasharray="donutCircumference"
                    :stroke-dashoffset="getDonutOffset(getOverallPercent())"
                  />
                </svg>
                <div class="donut-center">
                  <div class="donut-percent" :style="{ color: getStatusHex(getOverallPercent()) }">
                    {{ getOverallPercent().toFixed(2) }}%
                  </div>
                  <div class="donut-caption">{{ getStatusLabel(getOverallPercent()) }}</div>
                </div>
              </div>
              <div class="summary-stats">
                <div class="stat-block">
                  <div class="stat-icon bg-primary-soft"><vue-feather type="users" size="18"></vue-feather></div>
                  <div>
                    <div class="stat-num">{{ getTotalRegistered().toLocaleString() }}</div>
                    <div class="stat-label">สมัครแล้ว</div>
                  </div>
                </div>
                <div class="stat-block">
                  <div class="stat-icon bg-secondary-soft"><vue-feather type="target" size="18"></vue-feather></div>
                  <div>
                    <div class="stat-num">{{ getTotalTarget().toLocaleString() }}</div>
                    <div class="stat-label">เป้าหมายรวม</div>
                  </div>
                </div>
                <div class="stat-block">
                  <div class="stat-icon bg-success-soft"><vue-feather type="user-check" size="18"></vue-feather></div>
                  <div>
                    <div class="stat-num">{{ getTotalRemaining().toLocaleString() }}</div>
                    <div class="stat-label">ที่ว่างคงเหลือ</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section title -->
            <div class="section-title" v-if="report_runner_type.length > 0">
              <vue-feather type="activity" size="18"></vue-feather>
              <span>ความคืบหน้าการรับสมัคร แต่ละระยะ</span>
            </div>

            <!-- Loading -->
            <div v-if="is_loading_runner_type === 'Y' && report_runner_type.length === 0" class="state-box">
              <div class="text-center">
                <img style="width: 60px" src="../../assets/images/running_loading.svg" />
                <div class="mt-3 text-muted">กำลังโหลดข้อมูล...</div>
              </div>
            </div>

            <!-- Distance cards -->
            <div v-else-if="report_runner_type.length > 0" class="row g-3">
              <div v-for="item in report_runner_type" :key="item.id" class="col-md-6">
                <div class="dist-card" :style="{ '--accent': getStatusHex(getItemPercent(item)) }">
                  <div class="dist-accent"></div>
                  <div class="dist-body">
                    <div class="dist-head">
                      <div class="dist-name" :title="stripHtml(item.name)">{{ stripHtml(item.name) }}</div>
                      <span class="dist-badge" :class="'badge-' + getStatusColor(getItemPercent(item))">
                        {{ getStatusLabel(getItemPercent(item)) }}
                      </span>
                    </div>
                    <div class="dist-numbers">
                      <span class="dist-current">{{ Number(item.registered_count || 0).toLocaleString() }}</span>
                      <span class="dist-target">/ {{ Number(item.slots_target || 0).toLocaleString() }} คน</span>
                      <span class="dist-percent" :style="{ color: getStatusHex(getItemPercent(item)) }">
                        {{ getItemPercent(item).toFixed(2) }}%
                      </span>
                    </div>
                    <div class="dist-progress">
                      <div
                        class="dist-progress-bar"
                        :style="{ width: Math.min(getItemPercent(item), 100) + '%', background: getStatusHex(getItemPercent(item)) }"
                      ></div>
                    </div>
                    <div class="dist-foot">
                      <template v-if="getRemaining(item) > 0">
                        <vue-feather type="user-plus" size="13"></vue-feather>
                        เหลืออีก <b>{{ getRemaining(item).toLocaleString() }}</b> ที่นั่ง
                      </template>
                      <template v-else>
                        <vue-feather type="check-circle" size="13"></vue-feather>
                        เต็มแล้ว
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty -->
            <div v-else class="state-box">
              <div class="text-center text-muted">
                <vue-feather type="inbox" size="48" class="mb-3"></vue-feather>
                <div>ไม่พบข้อมูลผู้สมัคร</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      is_authorization: null,
      token: null,
      eventDetails: {},
      is_loading_runner_type: "N",
      report_runner_type: [],
      view_count: 0,
      refreshTimer: null,
      isAutoRefreshEnabled: false,
      lastUpdatedAt: "",
      donutCircumference: 2 * Math.PI * 52,
    };
  },
  async mounted() {
    this.token = localStorage.getItem("_rr_token");
    if (this.token !== null) {
      await this.authorization();
      if (this.is_authorization === "Y") {
        await this.getEventDetail(this.$route.params.event_key);
        if (this.eventDetails.is_enable_public_link !== "Y") {
          this.is_authorization = "N";
          return;
        }
        await this.load_runner_type();
        this.applyAutoRefreshState();
      }
    } else {
      this.$router.push("/auth/login");
    }
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  beforeUnmount() {
    this.stopAutoRefresh();
  },
  methods: {
    startAutoRefresh() {
      this.stopAutoRefresh();
      this.refreshTimer = setInterval(() => {
        this.load_runner_type();
      }, 3000);
    },
    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },
    applyAutoRefreshState() {
      if (this.isAutoRefreshEnabled) {
        this.startAutoRefresh();
      } else {
        this.stopAutoRefresh();
      }
    },
    onAutoRefreshSwitchChange() {
      this.applyAutoRefreshState();
    },
    async authorization() {
      const data = {
        event_key: this.$route.params.event_key,
      };
      const headers = {
        Authorization: "Bearer " + this.token,
      };
      const res = await axios.post("v1/organizer/authorization", data, { headers });
      if (res.data.status === "success") {
        this.is_authorization = res.data.message.is_authorization;
      } else {
        alert("พบข้อผิดพลาด[กรุณาแจ้งผู้ดูแลระบบ]: " + res.data.message);
      }
    },
    async getEventDetail(event_key) {
      const res = await axios.get("/v1/events/initial/" + event_key);
      if (res.data.status === "success") {
        this.eventDetails = res.data.body[0] || {};
      }
    },
    async load_runner_type() {
      this.is_loading_runner_type = "Y";
      const data = {
        event_key: this.$route.params.event_key,
      };
      const headers = {
        Authorization: "Bearer " + this.token,
      };

      const [res, viewRes] = await Promise.all([
        axios.post("v1/organizer/report-runner-type-realtime", data, { headers }),
        axios.post("v1/organizer/report-runner-type-view-count-realtime", data, { headers }),
      ]);

      if (res.data.status === "success") {
        this.report_runner_type = res.data.body || [];
        this.lastUpdatedAt = new Date().toLocaleString("th-TH");
      } else {
        alert("พบข้อผิดพลาด[กรุณาแจ้งผู้ดูแลระบบ]: load_runner_type");
      }

      if (viewRes.data.status === "success") {
        this.view_count = viewRes.data.view_count || 0;
      }

      this.is_loading_runner_type = "N";
    },
    getItemPercent(item) {
      const target = parseInt(item.slots_target || 0);
      const registered = parseInt(item.registered_count || 0);
      if (target <= 0) return 0;
      return Math.round((registered / target) * 10000) / 100;
    },
    getRemaining(item) {
      const target = parseInt(item.slots_target || 0);
      const registered = parseInt(item.registered_count || 0);
      return Math.max(target - registered, 0);
    },
    getStatusColor(percent) {
      if (percent >= 100) return "danger";
      if (percent >= 80) return "warning";
      if (percent >= 50) return "info";
      return "success";
    },
    getStatusHex(percent) {
      if (percent >= 100) return "#dc3545";
      if (percent >= 80) return "#f59e0b";
      if (percent >= 50) return "#0ea5e9";
      return "#22c55e";
    },
    getDonutOffset(percent) {
      const capped = Math.min(Math.max(percent, 0), 100);
      return this.donutCircumference * (1 - capped / 100);
    },
    getStatusLabel(percent) {
      if (percent >= 100) return "เต็มแล้ว";
      if (percent >= 80) return "ใกล้เต็ม";
      if (percent >= 50) return "ครึ่งทาง";
      return "ยังว่าง";
    },
    getTotalRegistered() {
      return this.report_runner_type.reduce((total, item) => total + parseInt(item.registered_count || 0), 0);
    },
    getTotalTarget() {
      return this.report_runner_type.reduce((total, item) => total + parseInt(item.slots_target || 0), 0);
    },
    getTotalRemaining() {
      return Math.max(this.getTotalTarget() - this.getTotalRegistered(), 0);
    },
    getOverallPercent() {
      const target = this.getTotalTarget();
      if (target <= 0) return 0;
      return Math.round((this.getTotalRegistered() / target) * 10000) / 100;
    },
    stripHtml(html) {
      if (!html) return "";
      const tmp = document.createElement("DIV");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
    },
  },
};
</script>

<style scoped>
.realtime-body {
  background: #f7f8fc;
  border-radius: 0.75rem;
  overflow: hidden;
  border: 1px solid #eceef4;
}

/* ===== Hero Header ===== */
.hero-header {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 24px 30px;
  background: #fff;
  color: #1f2937;
  border-bottom: 1px solid #eef0f5;
}

.hero-header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #4f46e5, #06b6d4);
}

.hero-left {
  position: relative;
  z-index: 1;
}

.live-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #dc2626;
  background: #fee2e2;
  padding: 3px 10px;
  border-radius: 20px;
  margin-bottom: 10px;
}

.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.7);
  animation: livePulse 1.5s infinite;
}

@keyframes livePulse {
  0% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.7); }
  70% { box-shadow: 0 0 0 8px rgba(74, 222, 128, 0); }
  100% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0); }
}

.hero-title {
  font-size: 25px;
  font-weight: 800;
  margin: 0;
  line-height: 1.2;
  color: #111827;
  letter-spacing: -0.3px;
}

.hero-sub {
  font-size: 14px;
  color: #6b7280;
  margin-top: 4px;
}

.hero-updated {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #9ca3af;
  margin-top: 10px;
}

.hero-right {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
}

.hero-refresh {
  font-weight: 600;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.auto-refresh-toggle {
  background: #f3f4f6;
  padding: 6px 12px;
  border-radius: 8px;
}

.auto-refresh-toggle .form-check-label {
  color: #374151;
  cursor: pointer;
}

.spinning {
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ===== Content ===== */
.realtime-content {
  padding: 24px 30px 30px;
}

/* ===== Summary card with donut ===== */
.summary-card {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 30px;
  background: #fff;
  border-radius: 18px;
  padding: 26px 32px;
  border: 1px solid #eef0f5;
  box-shadow: 0 10px 30px rgba(17, 24, 39, 0.05);
  margin-bottom: 26px;
}

.view-count-badge {
  position: absolute;
  top: 14px;
  right: 16px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid #e5e7eb;
}

.donut-wrap {
  position: relative;
  width: 160px;
  height: 160px;
  flex-shrink: 0;
}

.donut-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.donut-track {
  fill: none;
  stroke: #eef0f5;
  stroke-width: 12;
}

.donut-value {
  fill: none;
  stroke-width: 12;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.7s ease, stroke 0.3s ease;
}

.donut-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.donut-percent {
  font-size: 26px;
  font-weight: 800;
  line-height: 1;
}

.donut-caption {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}

.summary-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 26px;
  flex: 1;
}

.stat-block {
  display: flex;
  align-items: center;
  gap: 12px;
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bg-primary-soft { background: #eef2ff; color: #4f46e5; }
.bg-secondary-soft { background: #f1f5f9; color: #64748b; }
.bg-success-soft { background: #ecfdf5; color: #22c55e; }

.stat-num {
  font-size: 24px;
  font-weight: 800;
  color: #111827;
  line-height: 1.1;
}

.stat-label {
  font-size: 12px;
  color: #6b7280;
}

/* ===== Section title ===== */
.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 16px;
}

.section-title .feather {
  color: #7c3aed;
}

/* ===== Distance cards ===== */
.dist-card {
  position: relative;
  display: flex;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #eef0f5;
  box-shadow: 0 4px 16px rgba(17, 24, 39, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  height: 100%;
}

.dist-card:hover {
  transform: translateY(-4px);
  border-color: var(--accent);
  box-shadow: 0 14px 30px rgba(17, 24, 39, 0.1);
}

.dist-accent {
  width: 6px;
  background: var(--accent);
  flex-shrink: 0;
}

.dist-body {
  flex: 1;
  padding: 16px 18px;
}

.dist-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
}

.dist-name {
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.35;
}

.dist-badge {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 20px;
  white-space: nowrap;
}

.badge-success { background: #ecfdf5; color: #16a34a; }
.badge-info { background: #e0f2fe; color: #0284c7; }
.badge-warning { background: #fef3c7; color: #d97706; }
.badge-danger { background: #fee2e2; color: #dc2626; }

.dist-numbers {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 10px;
}

.dist-current {
  font-size: 26px;
  font-weight: 800;
  color: #111827;
  line-height: 1;
}

.dist-target {
  font-size: 13px;
  color: #6b7280;
}

.dist-percent {
  margin-left: auto;
  font-size: 18px;
  font-weight: 800;
}

.dist-progress {
  height: 10px;
  background: #eef0f5;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 10px;
}

.dist-progress-bar {
  height: 100%;
  border-radius: 20px;
  transition: width 0.7s ease;
}

.dist-foot {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  color: #6b7280;
}

.dist-foot .feather {
  color: var(--accent);
}

/* ===== State boxes ===== */
.state-box {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 260px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 3px 14px rgba(17, 24, 39, 0.05);
}

@media (max-width: 575px) {
  .hero-header,
  .realtime-content {
    padding-left: 16px;
    padding-right: 16px;
  }
  .hero-right {
    align-items: flex-start;
  }
}
</style>
